import { type ListResponse, type LoadingParams } from 'framework/list';
import type { ApiRequestOptions } from 'framework/api';
import type { CollectionPageResponse } from 'framework/ViewTypes/hooks';
import { useRecruitmentApi } from 'infrastructure/api';
import type { RecruitmentPool, RecruitmentPoolFormValues, RecruitmentPoolStatus } from './types';

const useRecruitmentPoolService = () => {
  const { sendPostRequest, sendPatchRequest, sendPutRequest } = useRecruitmentApi();

  const create = async (values: RecruitmentPoolFormValues): Promise<RecruitmentPool> => {
    const response = await sendPostRequest('/recruitment-pool', values);

    return response.json();
  };

  const update = async (id: string, values: RecruitmentPoolFormValues): Promise<RecruitmentPool> => {
    const response = await sendPutRequest(`/recruitment-pool/${id}`, values);

    return response.json();
  };

  const search = async (
    { pageNumber, pageSize, filter = {}, sort = {} }: LoadingParams,
    options?: ApiRequestOptions,
  ): Promise<CollectionPageResponse<RecruitmentPool>> => {
    const response = await sendPostRequest(
      '/recruitment-pool/search',
      {
        pageNumber,
        pageSize,
        filter,
        sort,
      },
      options,
    );
    const page = (await response.json()) as ListResponse<RecruitmentPool>;

    return {
      result: page.result ?? [],
      pageNumber: page.pageNumber ?? pageNumber,
      totalPages: page.totalPages ?? 0,
      totalElements: page.totalElements ?? page.result?.length ?? 0,
    };
  };

  const getById = async (id: string): Promise<RecruitmentPool | undefined> => {
    const page = await search({
      pageNumber: 0,
      pageSize: 1,
      filter: { id: { eq: id } },
    });

    return page.result[0];
  };

  const transit = async (id: string, status: RecruitmentPoolStatus): Promise<RecruitmentPool> => {
    const response = await sendPatchRequest(`/recruitment-pool/${id}/transit`, { status });

    return response.json();
  };

  return {
    create,
    update,
    search,
    getById,
    transit,
  };
};

export default useRecruitmentPoolService;
